import { useState } from "react";
import { cn } from "@/lib/utils";
import { assetUrl } from "@/lib/api";
import { NivaAvatar } from "@/components/NivaAvatar";

/** The face beside a chat bubble: Niva's portrait for her, the sender's profile
 *  photo when they have one, and their initials (gold for the BL side, slate
 *  for the buyer) when they don't — or when the photo fails to load. */
export function ChatSenderAvatar({
  role,
  name,
  src,
  className,
}: {
  role: string;
  name?: string | null;
  src?: string | null;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (role === "niva") return <NivaAvatar className={cn("h-7 w-7", className)} />;
  const url = src ? assetUrl(src) : null;
  if (url && !failed) {
    return (
      <img
        src={url}
        alt={name ?? ""}
        onError={() => setFailed(true)}
        className={cn("h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-border", className)}
      />
    );
  }
  const initials = (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";
  const blSide = role !== "buyer";
  return (
    <span
      className={cn(
        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
        blSide ? "bg-primary text-primary-foreground" : "bg-slate-400 text-white",
        className,
      )}
    >
      {initials}
    </span>
  );
}

const ROLE_LABELS: Record<string, string> = {
  buyer: "Buyer",
  bl_advisor: "BL Advisor",
  admin: "Admin",
  org_admin: "Admin",
  niva: "Niva",
};

/** "Buyer · Nolan Berend" / "BL Advisor · Philip …" / "Admin · …" over a chat
 *  bubble — the role in bold so a thread reads at a glance who is speaking for
 *  which side. Niva keeps her own "Niva · AI Deal Assistant" tag. */
export function ChatSenderLabel({
  role,
  name,
  className,
}: {
  role: string;
  name?: string | null;
  className?: string;
}) {
  if (role === "niva") {
    return (
      <span className={cn("text-[10px] text-muted-foreground", className)}>
        <span className="font-bold text-primary">Niva</span> · AI Deal Assistant
      </span>
    );
  }
  const label = ROLE_LABELS[role] ?? role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const who = name?.trim();
  return (
    <span className={cn("text-[10px] text-muted-foreground", className)}>
      <span className="font-bold text-foreground">{label}</span>
      {who && who !== label && <> · {who}</>}
    </span>
  );
}

export default ChatSenderLabel;
